// Signed-in device handling.
//
// Plain-language model: when someone logs in on a browser we have not seen
// before, we ask one question: "is this your own device, or one shared with
// others?" and remember the answer against that browser (not the account).
// Everything after that happens quietly in the background:
//
//   • shared device   → the sign-in ends at the end of the same day
//   • own device      → up to 7 days for Owner/Admin/safeguarding leads,
//                       up to 30 days for everyone else
//   • either way      → 8 hours of complete inactivity ends the sign-in
//
// Kiosk mode is deliberately untouched: it has its own 45-second reset and is
// not tied to an individual staff account.

export type DeviceChoice = "personal" | "shared";

const DEVICE_ID_KEY = "nursly_device_id";
const CHOICE_KEY = "nursly_device_trust";
const ACTIVITY_KEY = "nursly_last_activity";
const EXPIRY_KEY = "nursly_session_expires_at";
const SESSION_START_KEY = "nursly_session_started_at";

/** 8 hours of complete inactivity ends any sign-in. */
export const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000;

/** Maximum days on a personal device, by how much the account can reach. */
export const PERSONAL_DAYS_HIGH_ACCESS = 7;
export const PERSONAL_DAYS_STANDARD = 30;

const safeLocal = () => (typeof window === "undefined" ? null : window.localStorage);

/* ── Long-life mirror in a cookie ──────────────────────────────────────────
 * The device answer must survive far longer than browser storage sometimes
 * lasts. Safari (and "clear site data on close" settings) can quietly wipe
 * localStorage, which made the question come back for people who had already
 * answered "this is my device". Writing the same answer to a one-year cookie
 * and reading from whichever store still has it keeps the promise that we
 * only ask once per device.
 */
const COOKIE_MAX_AGE = 400 * 24 * 60 * 60; // browsers cap cookies at ~400 days

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/** Reads a remembered value from local storage, falling back to the cookie. */
function readRemembered(key: string): string | null {
  const local = safeLocal()?.getItem(key) ?? null;
  if (local) return local;
  const cookie = readCookie(key);
  if (cookie) {
    // Heal the missing local copy so later reads are fast.
    try {
      safeLocal()?.setItem(key, cookie);
    } catch {
      /* storage may be full or blocked */
    }
  }
  return cookie;
}

function writeRemembered(key: string, value: string) {
  try {
    safeLocal()?.setItem(key, value);
  } catch {
    /* storage may be full or blocked */
  }
  writeCookie(key, value);
}

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `d_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** A stable id for this browser. Created on first use. */
export function getDeviceId(): string {
  if (typeof window === "undefined") return "unknown-device";
  let id = readRemembered(DEVICE_ID_KEY);
  if (!id) {
    id = randomId();
    writeRemembered(DEVICE_ID_KEY, id);
  }
  return id;
}

export function getDeviceChoice(): DeviceChoice | null {
  const value = readRemembered(CHOICE_KEY);
  return value === "personal" || value === "shared" ? value : null;
}

export function setDeviceChoice(choice: DeviceChoice) {
  writeRemembered(CHOICE_KEY, choice);
}

export function clearDeviceChoice() {
  safeLocal()?.removeItem(CHOICE_KEY);
  deleteCookie(CHOICE_KEY);
}


/** A friendly description like "Chrome on iPad", no fingerprinting. */
export function describeDevice(): string {
  if (typeof navigator === "undefined") return "Unknown device";
  const ua = navigator.userAgent || "";
  const browser =
    /Edg\//.test(ua) ? "Edge"
    : /OPR\//.test(ua) ? "Opera"
    : /Chrome\//.test(ua) && !/Chromium/.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari"
    : "Browser";
  const platform =
    /iPad/.test(ua) ? "iPad"
    : /iPhone/.test(ua) ? "iPhone"
    : /Android/.test(ua) ? "Android"
    : /Macintosh|Mac OS X/.test(ua) ? "Mac"
    : /Windows/.test(ua) ? "Windows"
    : /Linux/.test(ua) ? "Linux"
    : "device";
  return `${browser} on ${platform}`;
}

/** Accounts that can reach the most: Owner, Admin, or anyone holding the DSL flag. */
export function isHighAccess(role: string | null | undefined, isDsl: boolean): boolean {
  return isDsl || role === "super_admin" || role === "admin";
}

/** End of the current calendar day, in the person's own time zone. */
export function endOfToday(now: Date = new Date()): Date {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return end;
}

/** When this sign-in should end, given the device answer and the account. */
export function computeExpiry(
  choice: DeviceChoice,
  role: string | null | undefined,
  isDsl: boolean,
  now: Date = new Date()
): Date {
  if (choice === "shared") return endOfToday(now);
  const days = isHighAccess(role, isDsl) ? PERSONAL_DAYS_HIGH_ACCESS : PERSONAL_DAYS_STANDARD;
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
}

/** Plain-language summary shown in Active sessions. */
export function expiryDescription(choice: DeviceChoice, role: string | null | undefined, isDsl: boolean): string {
  if (choice === "shared") return "Signs out automatically at the end of the day";
  const days = isHighAccess(role, isDsl) ? PERSONAL_DAYS_HIGH_ACCESS : PERSONAL_DAYS_STANDARD;
  return `Stays signed in for up to ${days} days`;
}

/* ── Local activity + expiry bookkeeping (survives a page refresh) ────── */

export function markActivity(when: Date = new Date()) {
  safeLocal()?.setItem(ACTIVITY_KEY, String(when.getTime()));
}

export function getLastActivity(): number | null {
  const raw = safeLocal()?.getItem(ACTIVITY_KEY);
  const value = raw ? Number(raw) : NaN;
  return Number.isFinite(value) ? value : null;
}

export function setLocalExpiry(when: Date) {
  safeLocal()?.setItem(EXPIRY_KEY, when.toISOString());
}

export function getLocalExpiry(): Date | null {
  const raw = safeLocal()?.getItem(EXPIRY_KEY);
  if (!raw) return null;
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** When this sign-in began on this browser. Used to ignore stale leftovers. */
export function markSessionStart(when: Date = new Date()) {
  safeLocal()?.setItem(SESSION_START_KEY, String(when.getTime()));
}

export function getSessionStart(): number | null {
  const raw = safeLocal()?.getItem(SESSION_START_KEY);
  const value = raw ? Number(raw) : NaN;
  return Number.isFinite(value) ? value : null;
}

export function clearSessionTracking() {
  const store = safeLocal();
  store?.removeItem(ACTIVITY_KEY);
  store?.removeItem(EXPIRY_KEY);
  store?.removeItem(SESSION_START_KEY);
}

/**
 * Is a saved signal (an end time or a sign-out from elsewhere) left over from a
 * previous sign-in on this browser? Anything dated at or before the moment this
 * sign-in began must never end the sign-in that just started: that is what was
 * bouncing people straight back to the login screen.
 */
export function isStaleSignal(signalAt: Date | null, sessionStart: number | null): boolean {
  if (!signalAt) return false;
  if (sessionStart == null) return false;
  return signalAt.getTime() <= sessionStart;
}

/** Has this sign-in run out of time, or been left completely idle too long? */
export function sessionEndReason(
  expiresAt: Date | null,
  lastActivity: number | null,
  now: number = Date.now()
): "expired" | "idle" | null {
  if (expiresAt && now >= expiresAt.getTime()) return "expired";
  if (lastActivity != null && now - lastActivity >= IDLE_LIMIT_MS) return "idle";
  return null;
}
